import { DeepfakeIcon, LivenessIcon } from '@vlabs/icons';

import { EstimationStatus } from '../../components/person-selector/EstimationStatus';

// FIXME: это временный файл, требуется рефакторинг
// задача https://jira.visionlabs.ru/browse/GUI-3177

const HOVER_FILL = 'rgba(184, 191, 205, 0.5)';

const createBboxStyles = (stroke) => ({
  base: { stroke, strokeWidth: 2 },
  hover: { stroke, strokeWidth: 3, fill: HOVER_FILL },
  // выбранный bbox прозрачен: затемнение рисуется маской вокруг человека
  selected: { stroke, strokeWidth: 3, fill: 'transparent' },
});

const ERROR_STYLES = createBboxStyles('rgba(255, 61, 51, 1)');
const SUCCESS_STYLES = createBboxStyles('rgba(34, 196, 93, 1)');

const getBboxStyle = (liveness, deepfake) => {
  const hasError = (liveness && liveness !== 'real') || (deepfake && deepfake !== 'real');
  return hasError ? ERROR_STYLES : SUCCESS_STYLES;
};

const MIN_DETECTION_SCORE_THRESHOLD = 0.4;

const parseBoundingBoxes = (events) => {
  const boundingBoxes = {};
  if (!events || events.length === 0) return [];

  events?.forEach((event) => {
    const liveness = event?.aggregated_estimations.face?.liveness?.prediction;
    const deepfake = event?.aggregated_estimations.face?.deepfake?.prediction;
    const style = getBboxStyle(liveness, deepfake);

    event.frames_estimations.forEach(({ time_offset, estimated }) => {
      const timestamp = time_offset.toFixed(2);

      if (!boundingBoxes[timestamp]) boundingBoxes[timestamp] = [];

      const faceDetection = estimated?.face?.detection;
      const bodyDetection = estimated?.body?.detection;

      if (faceDetection?.bbox && faceDetection?.score > MIN_DETECTION_SCORE_THRESHOLD) {
        boundingBoxes[timestamp].push({
          type: 'face',
          event_id: event.event_id,
          ...faceDetection.bbox,
          id: String(event.event_id),
          style,
        });
      }

      if (bodyDetection?.bbox && bodyDetection?.score > MIN_DETECTION_SCORE_THRESHOLD) {
        boundingBoxes[timestamp].push({
          type: 'body',
          event_id: event.event_id,
          ...bodyDetection.bbox,
          id: String(event.event_id),
          style,
        });
      }
    });
  });

  return Object.fromEntries(
    Object.entries(boundingBoxes).sort(([a], [b]) => Number(a) - Number(b)),
  );
};

async function cropImageByBBox(imageSrc, detection, size = 72) {
  const { face, body } = detection;
  const bbox = face?.bbox || body?.bbox;
  const {
    x, y, width, height,
  } = bbox;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const cropX = Math.max(0, x);
      const cropY = Math.max(0, y);
      const cropW = Math.min(width, img.width - cropX);
      const cropH = Math.min(height, img.height - cropY);

      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');

      const scale = Math.min(size / cropW, size / cropH);
      const drawW = cropW * scale;
      const drawH = cropH * scale;
      const offsetX = (size - drawW) / 2;
      const offsetY = (size - drawH) / 2;

      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropW,
        cropH,
        offsetX,
        offsetY,
        drawW,
        drawH,
      );

      resolve(canvas.toDataURL());
    };
    img.onerror = reject;
    img.src = imageSrc;
  });
}

function getEstimationsStatusComponent(faceEstimations) {
  if (!faceEstimations) return;
  const components = [];

  if (faceEstimations?.liveness) {
    components.push(<EstimationStatus
      estimationType="liveness"
      icon={<LivenessIcon />}
      key="LivenessIcon"
      status={faceEstimations.liveness.prediction}
    />);
  }
  if (faceEstimations?.deepfake) {
    components.push(<EstimationStatus
      estimationType="deepfake"
      icon={<DeepfakeIcon />}
      key="DeepfakeIcon"
      status={faceEstimations.deepfake.prediction}
    />);
  }

  return components;
}

async function parseEventsToPersonList(events) {
  const personList = await Promise.all(
    events.map(async (e) => {
      const imageSrc = `data:image/jpeg;base64,${e.overview.image}`;
      const croppedSrc = await cropImageByBBox(imageSrc, e.overview.detection);

      return {
        segmentStartTime: e.video_segment.start_time_offset,
        segmentEndTime: e.video_segment.end_time_offset,
        id: e.event_id,
        src: croppedSrc,
        statusList: getEstimationsStatusComponent(e.aggregated_estimations.face),
      };
    }),
  );

  return personList;
}

const parseTracks = (events, mediaDurationMs, checksTargets) => {
  const livenessSegments = [];
  const deepfakeSegments = [];

  events.forEach((e) => {
    const lv = e.aggregated_estimations?.face?.liveness;
    if (lv) {
      livenessSegments.push({
        startTime: e.video_segment?.start_time_offset,
        endTime: e.video_segment?.end_time_offset,
        mediaDuration: mediaDurationMs,
        status: lv.prediction,
      });
    }

    const df = e.aggregated_estimations?.face?.deepfake;
    if (df) {
      deepfakeSegments.push({
        startTime: e.video_segment?.start_time_offset,
        endTime: e.video_segment?.end_time_offset,
        mediaDuration: mediaDurationMs,
        status: df.prediction,
      });
    }
  });

  const tracks = [];

  if (livenessSegments.length || checksTargets.includes('aggregated_liveness')) {
    tracks.push({
      segments: livenessSegments,
      title: 'LIVENESS',
    });
  }

  if (deepfakeSegments.length || checksTargets.includes('aggregated_deepfake')) {
    tracks.push({
      segments: deepfakeSegments,
      title: 'DEEPFAKE',
    });
  }

  return tracks;
};

export const handleResultData = async (values) => {
  const checksTargets = values?.request?.analytics[0]?.targets;
  const duration = values?.response?.video_data?.duration;
  const events = values?.response?.analytics?.[0]?.result?.tracking?.events;
  const personList = await parseEventsToPersonList(events);

  return {
    tracks: parseTracks(events, duration, checksTargets),
    // FIXME: add colors checks for lf and df
    boundingBoxes: parseBoundingBoxes(events),
    duration: Number(duration.toFixed(2)),
    personList,
    parameters: values?.request?.analytics?.[0]?.parameters,
    events,
  };
};
